import { useState } from 'react';
import { X, Search, Loader2, Check, Image, Upload, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDesignItems, useAddDesignLink, designItemKeys } from '../../hooks/useDesignItems';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  projectId: string;
  issueId: string;
  linkedDesignItemIds: Set<string>;
  onClose: () => void;
}

const SOURCE_ICON: Record<string, React.ReactNode> = {
  FIGMA: <Image size={12} />,
  UPLOAD: <Upload size={12} />,
  EXTERNAL_URL: <Link2 size={12} />,
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: '초안',
  IN_REVIEW: '리뷰 중',
  REVISION: '수정 요청',
  APPROVED: '승인',
};

export default function DesignLinkModal({ projectId, issueId, linkedDesignItemIds, onClose }: Props) {
  const queryClient = useQueryClient();
  const addLinkMutation = useAddDesignLink();

  const [search, setSearch] = useState('');
  const [linkType, setLinkType] = useState<'IMPLEMENTS' | 'REFERENCES'>('IMPLEMENTS');
  const [linking, setLinking] = useState<string | null>(null);

  const { data, isLoading } = useDesignItems(projectId, {
    search: search || undefined,
    limit: 20,
    page: 1,
  });

  const handleLink = async (designItemId: string) => {
    setLinking(designItemId);
    try {
      await addLinkMutation.mutateAsync({ projectId, designItemId, issueId, linkType });
      queryClient.invalidateQueries({ queryKey: designItemKeys.byIssue(projectId, issueId) });
      toast.success('디자인 항목이 연결되었습니다.');
    } catch {
      toast.error('연결에 실패했습니다.');
    }
    setLinking(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md max-h-[70vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">디자인 항목 연결</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Search + linkType */}
        <div className="px-4 pt-3 space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="디자인 항목 검색..."
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setLinkType('IMPLEMENTS')}
              className={`px-2.5 py-1 rounded text-sm font-medium border transition-colors ${
                linkType === 'IMPLEMENTS'
                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                  : 'bg-white border-gray-200 text-gray-600'
              }`}
            >
              구현 (IMPLEMENTS)
            </button>
            <button
              onClick={() => setLinkType('REFERENCES')}
              className={`px-2.5 py-1 rounded text-sm font-medium border transition-colors ${
                linkType === 'REFERENCES'
                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                  : 'bg-white border-gray-200 text-gray-600'
              }`}
            >
              참조 (REFERENCES)
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 animate-spin text-gray-400" />
            </div>
          ) : data?.items.length === 0 ? (
            <p className="text-center py-8 text-sm text-gray-500">
              {search ? '검색 결과가 없습니다.' : '디자인 항목이 없습니다.'}
            </p>
          ) : (
            <div className="space-y-1">
              {data?.items.map((item) => {
                const isLinked = linkedDesignItemIds.has(item.designItemId);
                return (
                  <div
                    key={item.designItemId}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${
                      isLinked ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50 cursor-pointer'
                    }`}
                    onClick={() => !isLinked && handleLink(item.designItemId)}
                  >
                    {/* Thumbnail */}
                    <div className="w-10 h-[30px] rounded bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                      {item.thumbnailPath ? (
                        <img src={`/${item.thumbnailPath}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-gray-400">{SOURCE_ICON[item.sourceType]}</span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{item.title}</p>
                      <p className="text-[11px] text-gray-400">{STATUS_LABEL[item.status] || item.status}</p>
                    </div>

                    {/* Action */}
                    {isLinked ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-50 text-green-700 text-[11px] rounded-full">
                        <Check size={10} /> 연결됨
                      </span>
                    ) : linking === item.designItemId ? (
                      <Loader2 className="size-4 animate-spin text-gray-400" />
                    ) : (
                      <span className="text-sm text-primary-600">연결</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
